import {
  Link,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ProgressBar from '@/components/shared/ProgressBar';
import AgentsSelection from '@/features/appointments/components/AgentsSelection';
import {
  APPOINTMENT_RESCHEDULE_MUTATION_KEY,
  type ModifyAppointmentResponseData,
  patchModifyAppointment,
} from '@/features/confirmation/api/patchConfirmation';
import type { RescheduleAppointmentData } from '@/features/confirmation/utils/ModifyAppointmentRequestData';
import useAgentsAndTimeslots from '@/features/scrollFlow/pages/Calendar/hooks/useAgentsAndTimeslots';
import useLandingPageRedirection from '@/hooks/useLandingPageRedirection';
import usePreselectOriginalAgent from '@/hooks/usePreselectOriginalAgent';
import useProgress from '@/hooks/useProgress';
import { Page } from '@/lib/tagging';
import { mapCommPref } from '@/lib/tagging/commPrefMapping';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore from '@/store/AppointmentStore';
import useSessionStore from '@/store/SessionStore';
import LocationSearchBox from './LocationSearchBox';

const RescheduleAgentSelection: React.FC = () => {
  const tagging = useTagging();
  const tagged = useRef(false);

  const selectedAppointmentType = useAppointmentStore(
    (state) => state.selectedAppointmentType,
  );
  const { agentsForSelectedTimeSlot, isLoading: isLoadingAgents } =
    useAgentsAndTimeslots();

  const selectedAgent = useAppointmentStore((state) => state.selectedAgent);
  const setSelectedAgent = useAppointmentStore(
    (state) => state.setSelectedAgent,
  );
  const setSelectedBranch = useAppointmentStore(
    (state) => state.setSelectedBranch,
  );
  const {
    selectedDestination,
    contactInfo,
    searchedDestination,
    userSearch,
    appointmentId,
    selectedStartTime,
    selectedEndTime,
    selectedDate,
    setConfirmedAppointment,
    setAppointmentId,
  } = useAppointmentStore();
  const { sessionId, sessionSource, setFlowType } = useSessionStore();

  const navigate = useNavigate();
  const { id: appointmentIdParam } = useParams<{ id: string }>();

  useLandingPageRedirection();
  useProgress(60);

  const isInBranchAppointment = selectedAppointmentType === 'in-branch';
  const isOnlineAppointment = selectedAppointmentType === 'online';
  const isButtonDisabled = !selectedAgent?.agentRecordId;

  usePreselectOriginalAgent(agentsForSelectedTimeSlot);

  useEffect(() => {
    if (!tagged.current) {
      const trvEndLocation =
        searchedDestination || userSearch || selectedDestination.name;
      tagging.tag('view', sessionSource, {
        page: Page.selectAgent,
        sessionid: sessionId,
        flow: 'modify',
        trv_end_location: trvEndLocation,
        comm_pref: mapCommPref(
          contactInfo.communicationPreference,
          isInBranchAppointment,
          isOnlineAppointment,
        ),
      });
      tagged.current = true;
    }
  }, [
    selectedDestination.name,
    sessionSource,
    sessionId,
    tagging.tag,
    contactInfo.communicationPreference,
    isInBranchAppointment,
    isOnlineAppointment,
    searchedDestination,
    userSearch,
  ]);

  useEffect(() => {
    if (isInBranchAppointment && selectedAgent?.location) {
      setSelectedBranch({
        branchId: selectedAgent.serviceTerritoryId ?? '',
        branchName: selectedAgent.location,
        branchAddress: '',
        distance: 0,
      });
    }
  }, [selectedAgent, isInBranchAppointment, setSelectedBranch]);

  const rescheduleAppointmentMutation = useMutation({
    mutationKey: APPOINTMENT_RESCHEDULE_MUTATION_KEY,
    mutationFn: (data: RescheduleAppointmentData) =>
      patchModifyAppointment(data),
    onSuccess: (
      data: ModifyAppointmentResponseData,
      variables: RescheduleAppointmentData,
    ) => {
      setAppointmentId(data.appointmentId);
      setConfirmedAppointment({
        agentInfo: selectedAgent,
        selectedDate,
        selectedStartTime: variables.startTime,
        selectedEndTime: variables.endTime,
      });
    },
  });

  const { isPending } = rescheduleAppointmentMutation;

  const handleClickContinue = () => {
    if (!selectedAgent?.agentRecordId || !selectedStartTime || !selectedEndTime)
      return;

    const rescheduleData: RescheduleAppointmentData = {
      agentRecordId: selectedAgent.agentRecordId ?? '',
      agentResourceId: selectedAgent.resourceId ?? '',
      appointmentId: appointmentIdParam ?? appointmentId ?? '',
      serviceTerritoryId: selectedAgent.serviceTerritoryId ?? '',
      startTime: selectedStartTime,
      endTime: selectedEndTime,
      status: 'Rescheduled',
    };

    setFlowType('modify');
    rescheduleAppointmentMutation.mutate(rescheduleData);
    navigate('/confirmation');
  };

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'background.default',
      }}
    >
      <Stack
        spacing={4}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          px: 2,
          pt: 4,
          overflow: 'hidden',
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: '100%',
            maxWidth: '600px',
            textAlign: 'left',
            minHeight: 0,
          }}
        >
          <Typography variant='h5'>
            {isInBranchAppointment
              ? 'Type in your preferred city or ZIP code.'
              : 'Please select an agent below.'}
          </Typography>

          {isInBranchAppointment && <LocationSearchBox />}

          <Stack sx={{ flex: 1, minHeight: 0 }}>
            {agentsForSelectedTimeSlot.length === 0 && !isLoadingAgents ? (
              <Typography variant='body1'>
                No nearby agents are available for the selected time slot.
                Please try{' '}
                <Link onClick={() => navigate(-1)}>
                  selecting a different time slot
                </Link>{' '}
                or adjust your location search criteria.
              </Typography>
            ) : (
              <AgentsSelection
                agents={agentsForSelectedTimeSlot}
                selectedAgent={selectedAgent}
                onSelectAgent={setSelectedAgent}
                isLoading={isLoadingAgents}
              />
            )}
          </Stack>
        </Stack>
      </Stack>

      <ProgressBar
        buttonDisabled={isButtonDisabled || isPending}
        displayBackButton={true}
        onClickContinue={handleClickContinue}
      />
    </Stack>
  );
};

export default RescheduleAgentSelection;
